<template>
  <div class="emergency-table">
    <b-modal
      id="exportingProgress"
      hide-footer
      hide-header
      size="lg"
      :scrollable="false"
    >
      <ExportProgress />
    </b-modal>

    <PageHeader
      :items="items"
      :hasHeaderButton="true"
      class="page-top-header d-flex align-items-center vp-width-270"
    >
      <template #headerButton>
        <div class="d-flex align-items-center">
          <VpButton
            :text="$t('activeEmergencySessionPage.endSession')"
            :width="rtlEnabled ? '135px' : '127px'"
            :height="'27px'"
            :disabled="!haveEndPermission"
            v-if="!sessionEnded"
            @click="showEndSessionModal()"
          />

          <svg
            v-show="!sessionEnded && !haveEndPermission && !mountLoading"
            style="margin-inline-start: 16px; outline: none"
            v-b-tooltip
            id="disabled-icon"
            width="17"
            height="17"
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.49904 5.41102e-08C6.81794 0.00018973 5.17466 0.498867 3.77697 1.43297C2.37928 2.36708 1.28996 3.69466 0.646766 5.24785C0.00356934 6.80103 -0.164642 8.51007 0.163441 10.1588C0.491523 11.8076 1.30114 13.3221 2.48992 14.5107C3.67871 15.6994 5.19327 16.5088 6.84208 16.8367C8.49089 17.1646 10.1999 16.9962 11.753 16.3529C13.3061 15.7095 14.6336 14.62 15.5676 13.2222C16.5015 11.8244 17 10.1811 17 8.49998C17 7.38367 16.7801 6.27828 16.3529 5.24696C15.9256 4.21563 15.2995 3.27855 14.5101 2.48924C13.7207 1.69993 12.7835 1.07385 11.7521 0.646742C10.7208 0.219634 9.61535 -0.000125898 8.49904 5.41102e-08ZM9.61057 12.9471C9.61057 13.2419 9.49347 13.5246 9.28502 13.733C9.07657 13.9415 8.79383 14.0586 8.49904 14.0586C8.20424 14.0586 7.92151 13.9415 7.71306 13.733C7.5046 13.5246 7.38751 13.2419 7.38751 12.9471V7.67234C7.38751 7.37754 7.5046 7.09481 7.71306 6.88635C7.92151 6.6779 8.20424 6.56079 8.49904 6.56079C8.79383 6.56079 9.07657 6.6779 9.28502 6.88635C9.49347 7.09481 9.61057 7.37754 9.61057 7.67234V12.9471ZM8.49904 5.16732C8.27976 5.16675 8.06555 5.10127 7.88342 4.97914C7.7013 4.85702 7.55939 4.68371 7.47561 4.48107C7.39183 4.27842 7.36991 4.05551 7.41262 3.84043C7.45534 3.62534 7.56078 3.42773 7.71563 3.27247C7.87049 3.11722 8.06784 3.01127 8.28281 2.968C8.49778 2.92474 8.72073 2.94609 8.9236 3.02935C9.12646 3.11261 9.30013 3.25405 9.42273 3.43586C9.54532 3.61767 9.61136 3.83171 9.6125 4.05099C9.61313 4.19748 9.58476 4.34264 9.52905 4.47812C9.47335 4.61361 9.39138 4.73673 9.28789 4.8404C9.18439 4.94407 9.06141 5.02625 8.92602 5.08219C8.79064 5.13813 8.64553 5.16673 8.49904 5.16635V5.16732Z"
              fill="#787878"
            />
          </svg>
        </div>

        <b-tooltip
          id="end-session-icon"
          class="lock-msg"
          target="disabled-icon"
          triggers="click blur"
          placement="rightbottom"
        >
          <ul style="margin-bottom: 0px">
            <li class="feature-availability" style="text-align: start">
              {{ $t("activeEmergencySessionPage.endingSessionNotAllowed") }}
            </li>
          </ul>
        </b-tooltip>
      </template>
    </PageHeader>

    <!-- grid -->
    <section
      class="emergency-data-list-table emergency-session-data-white-bg-sec"
      style="background-color: white"
    >
      <div
        class="d-flex datepicker-background"
        v-if="windowWidth > 768"
        style="padding: 24px 0"
      >
        <!-- begin::Column Chooser -->
        <div>
          <button
            class="custom-columns"
            @click="dataGrid.showColumnChooser(), positionColumnChooser()"
          >
            <span class="custom-text">{{ $t("common.grid.columns") }} </span>

            <span class="custom-icon">
              <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
            </span>
          </button>
        </div>
        <!-- end::Column Chooser -->

        <!-- begin:: Export -->
        <div class="export-dropdown">
          <DxDropDownButton
            :items="buttonData"
            @item-click="onItemClick"
            :text="$t('common.grid.export')"
          />
        </div>
        <!-- end::Export -->

        <!-- begin::Clear all -->
        <div v-if="showClearAll" class="d-flex clear-cols">
          <div class="toolbar-custom-line"></div>
          <Button
            :text="$t('common.grid.clearFilters')"
            @click="reset()"
            styling-mode="outlined"
            type="danger"
            class="clear-CustomColums"
          />
        </div>
      </div>
      <!-- end::Clear all -->

      <DxDataGrid
        id="gridEmergencySessionData"
        :data-source="windowWidth > 768 ? rowsData : []"
        :ref="dataGridRefName"
        :remote-operations="false"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="true"
        :showRowLines="true"
        :repaint-changes-only="true"
        :two-way-binding-enabled="true"
        :no-data-text="gridEmptyMessage"
        @editor-preparing="onEditorPreparing($event)"
        @content-ready="contentReady($event)"
        :visible="windowWidth > 768 ? true : false"
        style="height: calc(100vh - 272px)"
        @data-error-occurred="onDataErrorOccurred($event)"
        :rtl-enabled="rtlEnabled"
      >
        <DxScrolling mode="standard" :scroll-by-content="rtlEnabled" :use-native="!rtlEnabled" />
        <DxGroupPanel
          :visible="true"
          :empty-panel-text="$t('common.grid.groupPanelMessage')"
        />

        <DxStateStoring
          :enabled="true"
          type="custom"
          :customSave="customSave"
          :customLoad="customLoad"
        />

        <DxFilterRow :visible="true" />
        <DxColumnChooser
          :enabled="false"
          mode="select"
          :title="$t('common.grid.columnChooser')"
        />

        <DxHeaderFilter :visible="true" />

        <DxColumn
          data-field="image"
          :caption="$t('common.image')"
          cell-template="image-cell"
          :allow-filtering="false"
          :allow-editing="false"
          :show-in-column-chooser="true"
          :visible="true"
          data-type="object"
          alignment="center"
          css-class="image-index"
          :allow-exporting="false"
        />

        <DxColumn
          data-field="fullName"
          :caption="$t('common.fullName')"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
          :width="200"
          data-type="string"
        />

        <DxColumn
          data-field="status"
          :caption="$t('common.status')"
          :allow-editing="false"
          :visible="true"
          :allow-filtering="false"
          :allow-header-filtering="true"
          cell-template="status-cell"
          :filter-type="sessionEnded ? '' : filterType"
          :filter-values="
            sessionEnded ? filterValuesWithSessionEnded : filterValues
          "
        />

        <DxColumn
          data-field="host"
          :caption="$t('common.host')"
          :allow-filtering="true"
          :allow-header-filtering="true"
          :allow-exporting="true"
          :allow-editing="false"
          data-type="string"
          :visible="isFlowHasHost || isUserHasHost"
          :width="200"
          :filter-operations="allowedOperations"
        />

        <DxColumn
          data-field="phone"
          :caption="$t('common.phoneNumber')"
          :allow-filtering="false"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
          data-type="string"
        />

        <DxColumn
          data-field="site"
          :caption="$t('common.site')"
          :allow-filtering="true"
          :allow-header-filtering="true"
          :allow-editing="false"
          :visible="true"
          :filter-operations="allowedOperations"
          data-type="string"
        />

        <DxColumn
          data-field="area"
          :caption="$t('common.area')"
          :allow-filtering="true"
          :allow-header-filtering="true"
          :allow-editing="false"
          :visible="true"
          :filter-operations="allowedOperations"
          data-type="string"
        />

        <DxColumn
          data-field="userType"
          :caption="$t('common.userType')"
          :allow-editing="false"
          :visible="true"
          :filter-operations="allowedOperations"
          :allow-filtering="true"
          :allow-header-filtering="true"
          data-type="string"
        />

        <DxColumn
          data-field="by"
          :caption="$t('common.by')"
          :allow-filtering="false"
          :allow-exporting="true"
          :allow-editing="false"
          :visible="true"
          :width="200"
          data-type="string"
        />

        <DxColumn
          data-field="email"
          :caption="$t('common.emailAddress')"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
          :width="200"
          data-type="string"
        />

        <DxColumn
          data-field="rfid"
          :caption="$t('common.RFID')"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :visible="true"
          data-type="string"
          cell-template="rfid-template"
        />

        <template #rfid-template="{ data }">
          {{ data.data.rfid ? data.data.rfid : "-" }}
        </template>

        <DxColumn
          data-field="user_id"
          caption="ID"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          :allow-editing="false"
          :visible="false"
          data-type="string"
        />

        <template #image-cell="{ data }">
          <div v-viewer class="images dash-img clearfix">
            <ImageCell
              v-if="!data.data.image"
              :image="data.data.image"
              :name="data.data.fullName ? data.data.fullName.charAt(0) : ''"
            />

            <img
              class="img-fluid rounded-circle"
              v-if="data.data.image"
              style="cursor: pointer"
              :src="data.data.image"
            />
          </div>
        </template>

        <template #status-cell="{ data }">
          <div class="d-flex align-items-center">
            <div
              class="d-flex"
              v-if="data.data.sessionStatus == 'Stared'"
              style="height: 20px"
            >
              <v-radio-group
                @change="changeStatus($event, data.data.id)"
                v-model="data.data.status"
              >
                <v-radio
                  color="#34C38F"
                  :class="{
                    selected:
                      (data.data.id == rowId && rowValue == 'Present') ||
                      data.data.status == 'Present',
                    'text-success':
                      (data.data.id == rowId && rowValue == 'Present') ||
                      data.data.status == 'Present',
                  }"
                  :label="$t('emergencyList.grid.present')"
                  value="Present"
                ></v-radio>
                <v-radio
                  color="#F46A6A"
                  :class="{
                    selected:
                      (data.data.id == rowId && rowValue == 'Not Present') ||
                      data.data.status == 'Not Present',
                    'text-danger':
                      (data.data.id == rowId && rowValue == 'Not Present') ||
                      data.data.status == 'Not Present',
                  }"
                  :label="$t('emergencyList.grid.notPresent')"
                  value="Not Present"
                ></v-radio>
                <v-radio
                  :class="{
                    selected:
                      (data.data.id == rowId && rowValue == 'Out') ||
                      data.data.status == 'Out',
                    'text-blue':
                      (data.data.id == rowId && rowValue == 'Out') ||
                      data.data.status == 'Out',
                  }"
                  :label="$t('emergencyList.grid.out')"
                  value="Out"
                ></v-radio>
              </v-radio-group>
            </div>

            <div
              v-if="data.data.sessionStatus == 'Ended'"
              :class="
                data.data.status == 'Present'
                  ? 'text-success'
                  : data.data.status == 'Not Present'
                  ? 'text-danger'
                  : data.data.status == 'Out'
                  ? 'text-blue'
                  : ''
              "
              class="font-weight-bolder"
            >
              <span>{{ data.data.status }}</span>
            </div>
          </div>
        </template>
      </DxDataGrid>

      <DxDataGrid
        id="gridEmergencySessionDataMobView"
        :data-source="windowWidth <= 768 ? rowsData : []"
        :ref="dataGridRefNameMob"
        :remote-operations="false"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="true"
        :showRowLines="true"
        :repaint-changes-only="true"
        :two-way-binding-enabled="true"
        :no-data-text="gridEmptyMessage"
        @editor-preparing="onEditorPreparing($event)"
        :visible="windowWidth <= 768 ? true : false"
        @content-ready="contentReadyMobView($event)"
        @data-error-occurred="onDataErrorOccurred($event)"
        style="height: calc(100vh - 272px)"
        :rtl-enabled="rtlEnabled"
      >
        <DxStateStoring :enabled="true" type="custom" />

        <DxHeaderFilter :visible="true" />

        <DxFilterRow :visible="true" />

        <DxToolbar>
          <DxItem location="before" template="siteTemplate" />
          <DxItem location="before" template="areaTemplate" />
          <DxItem location="before" template="clearAllTemplateMobView" />
        </DxToolbar>

        <DxColumn
          data-field="fullName"
          :caption="$t('common.user')"
          cell-template="user-cell"
          :allow-filtering="true"
          :allow-editing="false"
          :visible="true"
          :allow-header-filtering="false"
          width="65%"
          data-type="object"
        />

        <DxColumn
          data-field="status"
          :caption="$t('common.status')"
          :allow-editing="false"
          :visible="true"
          :allow-filtering="false"
          :allow-header-filtering="true"
          cell-template="status-cell"
          :filter-type="sessionEnded ? '' : filterType"
          :filter-values="
            sessionEnded ? filterValuesWithSessionEnded : filterValues
          "
          width="35%"
          data-type="object"
        />

        <template #user-cell="{ data }">
          <div class="my-2 d-flex align-items-center p-1">
            <div
              v-viewer
              class="imageMobView"
              :class="data.data.image ? 'userImageInMobView' : ''"
            >
              <ImageCell
                v-if="!data.data.image"
                :image="data.data.image"
                :name="data.data.fullName ? data.data.fullName.charAt(0) : ''"
              />

              <img
                class="img-fluid rounded-circle"
                v-if="data.data.image"
                style="cursor: pointer"
                :src="data.data.image"
              />
            </div>

            <div class="userInformationInMobView">
              <div>
                <span>{{ data.data.fullName }}</span>
              </div>
              <br />
              <div>
                <span>{{ data.data.userType }}</span>
              </div>
              <br />
              <div
                v-if="(isFlowHasHost || isUserHasHost) && data.data.hostMobView"
              >
                <span> {{ data.data.hostMobView }} </span>
              </div>
              <br
                v-if="(isFlowHasHost || isUserHasHost) && data.data.hostMobView"
              />
              <div>
                <span>{{ data.data.site }}</span>
              </div>
              <br />
              <div>
                <span>{{ data.data.area }}</span>
              </div>
              <br />
              <div>
                <span>{{ data.data.phone }}</span>
              </div>
            </div>
          </div>
        </template>

        <template #status-cell="{ data }">
          <div class="d-flex align-items-center" style="margin-top: 10px">
            <div
              v-if="data.data.sessionStatus == 'Stared'"
              style="height: 20px"
            >
              <b-form-radio-group
                class="second-radio"
                @change="changeStatus($event, data.data.id)"
                v-model="data.data.status"
              >
                <b-form-radio
                  :name="'present' + data.data.user_id"
                  :class="
                    data.data.status == 'Not Present' ? 'text-danger' : ''
                  "
                  value="Not Present"
                  >{{ $t("emergencyList.grid.notPresent") }}</b-form-radio
                >
              </b-form-radio-group>
              <br />
              <b-form-radio-group
                class="first-radio"
                @change="changeStatus($event, data.data.id)"
                v-model="data.data.status"
              >
                <b-form-radio
                  :name="'present' + data.data.user_id"
                  :class="data.data.status == 'Present' ? 'text-success' : ''"
                  value="Present"
                  >{{ $t("emergencyList.grid.present") }}</b-form-radio
                >
              </b-form-radio-group>
              <br />
              <b-form-radio-group
                class="third-radio"
                @change="changeStatus($event, data.data.id)"
                v-model="data.data.status"
              >
                <b-form-radio
                  :name="'present' + data.data.user_id"
                  :class="data.data.status == 'Out' ? 'text-blue' : ''"
                  value="Out"
                  >{{ $t("emergencyList.grid.out") }}</b-form-radio
                >
              </b-form-radio-group>
            </div>

            <div
              v-if="data.data.sessionStatus == 'Ended'"
              :class="
                data.data.status == 'Present'
                  ? 'text-success'
                  : data.data.status == 'Not Present'
                  ? 'text-danger'
                  : data.data.status == 'Out'
                  ? 'text-blue'
                  : ''
              "
            >
              <span>{{ data.data.status }}</span>
            </div>
          </div>
        </template>

        <template #siteTemplate>
          <DxDropDownButton
            @item-click="filterBySite"
            :items="sitesMobViewData"
            class="custom-columns"
            display-expr="name"
            key-expr="id"
            :text="$t('common.site')"
          />
        </template>

        <template #areaTemplate>
          <DxDropDownButton
            @item-click="filterByArea"
            :items="areasMobViewData"
            class="custom-columns"
            display-expr="name"
            key-expr="id"
            :text="$t('common.area')"
          />
        </template>

        <template #clearAllTemplateMobView>
          <div v-if="showClearAllMobView" class="clear-cols">
            <Button
              :text="$t('common.grid.clearFilters')"
              @click="resetMobView()"
              styling-mode="outlined"
              type="danger"
              class="clear-CustomColums filter-mobile-view"
            />
          </div>
        </template>
      </DxDataGrid>
    </section>

    <!-- End Sessin Modal  -->
    <b-modal
      id="endSessionModal"
      class="popover-modal"
      hide-header
      hide-footer
      no-close-on-esc
      ref="endSessionModal"
    >
      <div class="content-cont">
        <p class="header-title">
          {{ $t("activeEmergencySessionPage.endSession") }}
        </p>
        <div class="hint-modal" v-html="$tc('emergencyList.endEmergencConfirm')" />
        <!-- action btns  -->
        <hr class="h-line" />
        <div class="action-btns">
          <button
            class="start-btn btn btn-primary d-flex align-items-center justify-content-center"
            @click="endSessionSubmission(id)"
            size="md"
          >
            {{ $t('common.end') }}
          </button>
          <button
            class="btn cancel-btn d-flex align-items-center justify-content-center"
            type="button"
            @click="$bvModal.hide('endSessionModal')"
          >
            {{ $t("common.cancel") }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
const dataGridRefName = "dataGridEmergencySessionListData";
const dataGridRefNameMob = "dataGridEmergencySessionListDataMobView";
import { mapActions, mapGetters } from "vuex";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import { emergencySessionListDataColumns } from "@/utils/dataSource";
import ImageCell from "@/components/sharedCells/ImageCell";
import "jspdf-autotable";
// import Breadcrumb from "@/components/breadcrumb/Breadcrumb";
import PageHeader from "@/components/page-header";
import ExportProgress from "@/components/grid/ExportProgress";
import { default as Button } from "devextreme-vue/button";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import { jsPDF } from "jspdf";
import amiriFont from "@/assets/fonts/Amiri-Regular.ttf"; // Load the Cairo font

import DxDataGrid, {
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxColumnChooser,
  DxToolbar,
  DxItem,
  DxStateStoring,
  DxScrolling,
  DxGroupPanel,
} from "devextreme-vue/data-grid";
import { rfidFormatterMixin } from "@/utils/mixins/rfidFormatterMixin";

export default {
  name: "EmergencyList",
  title: "Emergency List",
  mixins: [rfidFormatterMixin],
  components: {
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxColumnChooser,
    DxToolbar,
    DxItem,
    DxDropDownButton,
    ImageCell,
    // Breadcrumb,
    PageHeader,
    DxStateStoring,
    ExportProgress,
    Button,
    DxScrolling,
    DxGroupPanel,
  },
  data() {
    return {
      gridEmptyMessage : this.$t('common.grid.loadingData'),
      rtlEnabled: false,
      rowId: null,
      rowValue: null,
      haveEndPermission: false,
      mountLoading: true,
      sessionId: "",
      id: this.$route.params.sessionId,
      name: this.$route.params.sessionName,
      dataGridRefName,
      dataGridRefNameMob,
      allowedOperations: ["contains"],
      selectedOperation: "contains", // default icon
      buttonData: ["Excel", "CSV", "PDF"],
      emergencySessionListDataColumns: emergencySessionListDataColumns,
      statusHeaderFilter: [],
      areaHeaderFilter: [],
      userTypeHeaderFilter: [],
      selectedStatus: "",
      sessionEnded: true,
      diffAdminEndSession: "",
      userStartedSessionEmail: "",
      isEdit: false,
      filterType: "include", // or "exclude"
      filterValuesWithSessionEnded: ["Not Present", "Present", "Out"],
      filterValues: ["Not Present"],
      windowWidth: window.innerHeight,
      sitesMobViewData: [],
      areasMobViewData: [],
      filterBy: {
        sites: [],
        areas: [],
      },
      items: [
        {
          text: this.$t("sideNavbarMenu.emergencySessions.text"),
          to: { name: "EmergencySessionsList" },
        },
        {
          text: this.$route.params.sessionName,
          active: true,
          to: {
            name: "EmergencySeesionListData",
            params: {
              sessionName: this.$route.params.sessionName,
              sessionId: this.$route.params.sessionId,
            },
          },
        },
      ],
      isFlowHasHost: false,
      isUserHasHost: false,
      fileName: "Active Session List",
      showClearAll: false,
      showClearAllMobView: false,
    };
  },
  methods: {
    ...mapActions("grid", [
      "onTableHeader",
      "updateTableHeaders",
      "createTableHeaders",
      "exportJobBE",
      "onSetLoading",
      "checkVaccinePCRColumnAppearance",
    ]),
    ...mapActions("emergency", [
      "fetchSessionData",
      "getAllSession",
      "changeUserStatusSession",
      "endSession",
      "checkEndSessionPermission",
    ]),
    async customLoad() {
      if (this.userEmail != "lt_techteam@visipoint.com") {
        await this.onTableHeader({
          url: "/emergency_session_all_data",
        });
        let value = this.getTableHeaders;
        console.log("customLoad");
        return value;
      }
    },
    customSave(gridState) {
      if (this.userEmail != "lt_techteam@visipoint.com") {
        gridState.pageIndex = 0;
        gridState.pageSize = 1000000;
        let value = JSON.stringify(gridState);
        value = value.replace(',"selectedRowKeys":[]', "");

        if (!this.getTableHeaders) {
          this.createTableHeaders({
            url: "/emergency_session_all_data",
            headers: value,
            filters: "[]",
          });
        } else if (value != JSON.stringify(this.getTableHeaders)) {
          this.updateTableHeaders({
            url: "/emergency_session_all_data",
            headers: value,
            filters: "[]",
          });
        }
        console.log(
          "customSave value",
          value != JSON.stringify(this.getTableHeaders)
        );
      }
    },
    fetchTableHeaders() {
      this.onTableHeader({
        url: "/emergency_session_all_data",
        headers: emergencySessionListDataColumns,
      });
    },
    onToggleVisible() {
      this.$bvModal.show("emergencyListColumns");
    },
    contentReady(e) {
      let sortedExp = this.getSortedColumns();
      let filterExp = e.component.getCombinedFilter();
      if (filterExp || sortedExp) {
        this.showClearAll = true;
      } else {
        this.showClearAll = false;
      }
      this.setColumnChooserPosition();
    },
    contentReadyMobView(e) {
      let sortedExp = this.getSortedColumnsMobView();
      let filterExp = e.component.getCombinedFilter();
      if (filterExp || sortedExp) {
        this.showClearAllMobView = true;
      } else {
        this.showClearAllMobView = false;
      }
    },
    getSortedColumns() {
      let allColumns = Array.from(
        Array(this.dataGrid.columnCount()).keys()
      ).map((index) => this.dataGrid.columnOption(index));
      allColumns = allColumns.find((col) => col.sortIndex != null);
      return allColumns;
    },
    getSortedColumnsMobView() {
      let allColumns = Array.from(
        Array(this.dataGridMobView.columnCount()).keys()
      ).map((index) => this.dataGridMobView.columnOption(index));
      allColumns = allColumns.find((col) => col.sortIndex != null);
      return allColumns;
    },
    setColumnChooserPosition() {
      var columnChooserView = this.dataGrid.getView("columnChooserView");
      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }
      columnChooserView._popupContainer.option("position", {
        of: this.dataGrid.element(),
        my: this.rtlEnabled ? "right top" : "left top",
        at: this.rtlEnabled ? "right top" : "left top",
        offset: "0 50",
      });
    },
    onEditorPreparing(e) {
      if (e.parentType == "filterRow") {
        e.editorOptions.placeholder = this.$t("common.grid.search");
      }
    },
    showEndSessionModal() {
      this.$bvModal.show("endSessionModal");
    },
    async endSessionSubmission(id) {
      const { endSession } = this;
      await endSession({
        id: id,
        sessionId: this.id,
      });

      this.$bvModal.hide("endSessionModal");

      await this.fetchSessionData({
        id: this.id,
        params: {
          sites: [],
          areas: [],
        },
      });

      this.sessionStatus();

      // this.$socket.emit("pingEndSessionDiffAdmin", {
      //     sessionId : this.id,
      //     sessionName : this.name,
      //     entity : this.currentEntity.id,
      //     userIdEnded : this.userDetails.user_id,
      // });

      this.dataGrid.refresh().then(function () {
        console.log("refreshed");
      });
      // this.dataGrid.columnOption('status', 'filterValues', null);
    },
    sessionStatus() {
      this.sessionEnded = this.getSessionData?.data?.session?.status == "Ended";
    },
    changeStatus(value, id) {
      let newValue = "";
      this.rowId = id;
      this.rowValue = value;
      const { changeUserStatusSession } = this;
      if (value == "Present") {
        newValue = "PRESENT";
      } else if (value == "Not Present") {
        newValue = "NOT_PRESENT";
      } else if (value == "Out") {
        newValue = "OUT";
      }
      changeUserStatusSession({
        id: id,
        found: newValue,
        sessionId: this.id,
      }).then(() => {
        this.fetchSessionData({
          id: this.id,
          params: this.filterBy,
        });
      });
    },
    serverSideExport(e) {
      // console.log(e);
      const columnNames = this.dataGrid.getVisibleColumns();
      const columns = [];
      for (let i = 0; i < columnNames.length; i++) {
        if (columnNames[i].allowExporting) {
          columns.push(columnNames[i].caption);
        }
      }
      this.exportJobBE({
        type: e.itemData,
        headers: columns.reduce((accumulator, value) => {
          return { ...accumulator, [value]: value };
        }, {}),
        gridName: "emergencySession",
        emergency_session_id: this.id,
        entity_id: this.currentEntity.id,
        filterBy: {},
        orderBy: {
          sortField: null,
          sortOrder: null,
        },
      });
    },
    onItemClick(e) {
      e.rtlEnabled = this.rtlEnabled;
      if (e.itemData == "Excel") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");
        exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: (options) => {
            const excelCell = options;
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              `${this.fileName}.xlsx`
            );
          });
        });
        // e.cancel = true;
      } else if (e.itemData == "CSV") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");

        exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: (options) => {
            const excelCell = options;
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
          },
        }).then(() => {
          workbook.csv.writeBuffer().then((buffer) => {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              `${this.fileName}.csv`
            );
          });
        });
      } else if (e.itemData == "PDF") {
        const doc = new jsPDF('landscape');
        
        // Add custom font
        doc.addFont(amiriFont, "Amiri", "normal");
        doc.setFont("Amiri");

        doc.autoTable({ direction: "rtl" });

        exportDataGridToPdf({
          jsPDFDocument: doc,
          component: this.dataGrid,
          customizeCell: function (options) {
            const { gridCell, pdfCell } = options;

            if (gridCell.rowType === "data") {
              // data columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                cellWidth: "auto",
                halign: "center",
              };
            } else {
              // header columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                fillColor: "#2680EB",
                textColor: "#FFFFFF",
                cellWidth: "auto",
                halign: "center", 
              };
            }
          },
          customizeTable: (doc) => {
            doc.autoTable({ direction: "rtl" }); // Ensure table follows RTL direction
          },
        }).then(() => {
          const pageCount = doc.internal.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const pageSize = doc.internal.pageSize;
            const pageWidth = pageSize.width
              ? pageSize.width
              : pageSize.getWidth();
            const pageHeight = pageSize.height
              ? pageSize.height
              : pageSize.getHeight();
            const header = this.fileName;
            const headerWidth = doc.getTextDimensions(header).w;
            const footer = `${this.$t("common.page")} ${i} ${this.$t("common.of")} ${pageCount}`;

            // Header
            doc.text(header, (pageWidth - headerWidth) / 2, 10);

            // Footer
            doc.text(
              footer,
              pageWidth / 2 - doc.getTextWidth(footer) / 2,
              pageHeight - 5,
              { baseline: "bottom" }
            );
          }

          doc.save(`${this.fileName}.pdf`);
        });
      }
    },
    positionColumnChooser() {
      const columnChooserView = this.dataGrid.getView("columnChooserView");

      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }

      columnChooserView._popupContainer.option("position", {
        of: this.dataGrid.element(),
        my: "left top",
        at: "left top",
        offset: "0 10",
      });
    },
    // onItemClick(e) {
    //     if (e.itemData == "Excel") this.exportXLSX();
    //     else if (e.itemData == "CSV") this.exportCSV();
    //     else if (e.itemData == "PDF") this.exportPDF();
    // },
    // async exportPDF() {
    //     await this.getAllSession({
    //         sortObject: this.sortObject,
    //         finalFilterObject: this.finalFilterObject,
    //         limit: this.totalCount,
    //         isExporting: true,
    //         params : {
    //             sites : [],
    //             areas : [],
    //         }
    //     });
    //     const columnNames = this.dataGrid.getVisibleColumns();
    //     const columns = [];
    //     for (let i = 0; i < columnNames.length; i++) {
    //         if (columnNames[i].allowExporting)
    //         columns.push(columnNames[i].caption);
    //     }
    //     const doc = new jsPDF("landscape");
    //     // this.onSetLoading({ value: true });
    //     const { extractedData } = this;
    //     const headers = columns;
    //     this.pdfTableHeaders = headers;
    //     doc.autoTable({
    //         columns: this.pdfTableHeaders,
    //         body: extractedData("pdf"),
    //         styles: {
    //         fontSize: this.onSetPdfFont(this.pdfTableHeaders.length),
    //         cellWidth: "wrap",
    //         },
    //         didParseCell: (data) => {
    //         if (
    //             data.column.index === 0 &&
    //             data.cell.section === "body" &&
    //             headers.includes("Image")
    //         ) {
    //             data.cell.styles.minCellHeight = 10;
    //             data.cell.styles.minCellWidth = 10;
    //             data.cell.text = "";
    //         }
    //         },
    //         didDrawCell: (data) => {
    //         // Header
    //         doc.setFontSize(15);
    //         doc.text(this.fileName, 10, 10);

    //         if (
    //             data.column.index === 0 &&
    //             data.cell.section === "body" &&
    //             data.row.raw[0] != null
    //         ) {
    //             doc.addImage(
    //             data.row.raw[0],
    //             "JPEG",
    //             data.cell.x + 2,
    //             data.cell.y + 2,
    //             5,
    //             5
    //             );
    //         }
    //         },
    //     });
    //     doc.save(`${this.fileName}.pdf`);
    // },
    // extractedData(type) {
    //     const { rowsData } = this;
    //     const columnNames = this.dataGrid.getVisibleColumns();
    //     const columns = [];
    //     for (let i = 0; i < columnNames.length; i++) {
    //         if (columnNames[i].allowExporting)
    //         columns.push(columnNames[i].dataField);
    //     }
    //     const columnsObject = columns.reduce((a, v) => ({ ...a, [v]: v }), {});
    //     // console.log("columnsObject", columnsObject);
    //     const data = rowsData;
    //     // console.log("extracteddata", rowsData);
    //     return data && data.length > 0
    //         ? data.map((row) => {
    //             ["data", "Data", "Compliances"].forEach((e) => delete row[e]); // reomve unexported columns
    //             // const keys = Object.keys(row);
    //             const filterdRow = {};
    //             columns.forEach((key) => {
    //             if (key in columnsObject) {
    //                 return (filterdRow[key] = row[key]);
    //             }
    //             });
    //             return type == "pdf" ? Object.values(filterdRow) : filterdRow;
    //         })
    //         : [];
    // },
    // onSetPdfFont(length) {
    //     if (length >= 10) {
    //         return 4;
    //     } else if (length >= 8) {
    //         return 6;
    //     } else {
    //         return 8;
    //     }
    // },
    // exportXLSX() {
    //     this.isExport = true;
    //     const workbook = new Workbook();
    //     const worksheet = workbook.addWorksheet("Main sheet");

    //     exportDataGrid({
    //         component: this.dataGrid,
    //         worksheet,
    //         autoFilterEnabled: true,
    //         // topLeftCell: { row: 2, column: 2 },
    //         customizeCell: ({ gridCell, excelCell }) => {
    //         if (gridCell.rowType === "data") {
    //             if (gridCell.column.dataField === "image" && gridCell.value) {
    //             excelCell.value = undefined;

    //             const image = workbook.addImage({
    //                 base64: gridCell.value,
    //                 extension: "png",
    //             });

    //             worksheet.getRow(excelCell.row).height = 90;
    //             worksheet.addImage(image, {
    //                 tl: { col: excelCell.col - 1, row: excelCell.row - 1 },
    //                 br: { col: excelCell.col, row: excelCell.row },
    //             });
    //             }
    //         }
    //         },
    //     }).then(() => {
    //         workbook.xlsx.writeBuffer().then((buffer) => {
    //         saveAs(
    //             new Blob([buffer], { type: "application/octet-stream" }),
    //             `${this.fileName}.xlsx`
    //         );
    //         this.isExport = false;
    //         });
    //     });
    // },
    // exportCSV() {
    //     this.dataGrid.beginUpdate();
    //     const workbook = new Workbook();
    //     const worksheet = workbook.addWorksheet("Main sheet");

    //     exportDataGrid({
    //         component: this.dataGrid,
    //         worksheet: worksheet,
    //     }).then(() => {
    //         workbook.csv.writeBuffer().then((buffer) => {
    //         saveAs(
    //             new Blob([buffer], { type: "application/octet-stream" }),
    //             `${this.fileName}.csv`
    //         );
    //         this.dataGrid.endUpdate();
    //         });
    //     });
    // // e.cancel = true;
    // },
    filterBySite(e) {
      let sitesFiltered = [];
      sitesFiltered.push(e.itemData.id);
      this.filterBy.sites = sitesFiltered;
      this.fetchSessionData({
        id: this.id,
        params: this.filterBy,
      }).then(() => {
        this.sessionStatus();
      });

      this.dataGridMobView.refresh().then(function () {
        console.log("refreshed");
      });
    },
    filterByArea(e) {
      let areasFiltered = [];
      areasFiltered.push(e.itemData.id);
      this.filterBy.areas = areasFiltered;
      this.fetchSessionData({
        id: this.id,
        params: this.filterBy,
      }).then(() => {
        this.sessionStatus();
      });

      this.dataGridMobView.refresh().then(function () {
        console.log("refreshed");
      });
    },
    reset() {
      this.dataGrid.clearFilter("row");
      this.dataGrid.clearFilter("header");
      this.dataGrid.clearSorting();
    },
    resetMobView() {
      this.filterBy = {
        sites: [],
        areas: [],
      };
      this.fetchSessionData({
        id: this.id,
        params: this.filterBy,
      }).then(() => {
        this.sessionStatus();
      });
      this.dataGridMobView.clearFilter("row");
      this.dataGridMobView.clearFilter("header");
      this.dataGridMobView.clearSorting();
    },
    onDataErrorOccurred(e) {
      console.log("onDataErrorOccurred e", e);
      setTimeout(() => {
        let errorRow = document.querySelector(
          ".dx-datagrid-headers .dx-error-message"
        );
        errorRow.innerHTML = this.$t("common.notes.anErrorOccurredGrid");
      }, 100);
    },
  },
  watch: {
    getTableHeaders(value) {
      console.log("watch", value);
    },
    sessionEnded(value) {
      console.log(value);
    },
    getExportProgessAppearance(value) {
      value && this.$bvModal.show("exportingProgress");
    },
  },
  computed: {
    ...mapGetters("i18N", ["getLanguage"]),
    ...mapGetters("emergency", ["getSessionData"]),
    ...mapGetters("dailyLogbook", ["getAreas"]),
    ...mapGetters("grid", [
      "getTableHeaders",
      "getTableFilters",
      "getExportProgessAppearance",
      "getVaccinePCRColumnAppearance",
    ]),
    ...mapGetters("authfack", ["userDetails", "currentEntity"]),
    rowsData() {
      // console.log("sessionData", this.getSessionData);
      let sessionStatus = "Stared";
      if (this.getSessionData.data && this.getSessionData.data.session) {
        if (this.getSessionData.data.session.status == "Ended")
          sessionStatus = "Ended";
      }
      return this.getSessionData.data &&
        this.getSessionData.data.data &&
        this.getSessionData.data.data.length > 0
        ? this.getSessionData.data.data.map((row) => {
            let foundBy = "-";

            if (row.by && row.by != null && row.by != "-") {
              if (row.by !== "Admin - default user") {
                foundBy = row.by;
              } else {
                foundBy = "Admin - CS";
              }
            }

            return {
              id: row.id,
              user_id: row.user_user_id,
              image: row.image && row.image != null ? row.image : "",
              fullName:
                row.fullName && row.fullName != null ? row.fullName : "",
              phone: row.phone && row.phone != null ? row.phone : "-",
              userType:
                row.userType && row.userType != null ? row.userType : "",
              site: row.site && row.site != null ? row.site : "-",
              area: row.area && row.area != null ? row.area : "-",
              host:
                row.host && row.host != null
                  ? row.host + " (" + row.userTypeHost + ")"
                  : "-",
              hostMobView:
                row.host && row.host != null ? "Host (" + row.host + ")" : "-",
              email: row.email && row.email != null ? row.email : "-",
              rfid: row?.rf_ids?.length ? this.rfidFormatter(row.rf_ids) : null,
              status: row.status && row.status != null ? row.status : "",
              by: foundBy,
              sessionStatus: sessionStatus,
            };
          })
        : [];
    },
    dataGrid() {
      return this.$refs[this.dataGridRefName].instance;
    },
    dataGridMobView() {
      return this.$refs[this.dataGridRefNameMob].instance;
    },
    userEmail() {
      return this.userDetails && this.userDetails.email;
    },
    checkUserStartedSession() {
      let sameUserStartedAndEnded = false;
      if (this.getSessionData.data.session) {
        if (
          this.getSessionData.data.session.user_id_started !=
          this.userDetails.user_id
        ) {
          sameUserStartedAndEnded = true;
        }
      }
      return sameUserStartedAndEnded;
    },
  },
  async beforeMount() {
    if (this.getLanguage == "ar") {
      this.rtlEnabled = true;
    }
    this.checkEndSessionPermission(this.$route.params.sessionId).then(
      (data) => {
        this.haveEndPermission = data.data;
        this.mountLoading = false;
      }
    );
    await this.checkVaccinePCRColumnAppearance();
    await this.fetchSessionData({
      id: this.id,
      params: this.filterBy,
    }).then(() => {
      this.gridEmptyMessage = this.$t('common.grid.noRecordsYet');
    })
    this.sessionStatus();

    // console.log(this.sessionEnded);
    // if(!this.sessionEnded)
    // this.dataGrid.columnOption('status','filterValues',['Not Present'])
    // else
    //     this.dataGrid.columnOption('status','filterValues',[])
  },
  created() {
    document.getElementById("preloader").style.display = "none";
    document.getElementById("status").style.display = "none";
    this.windowWidth = window.innerWidth;

    if (
      this.getVaccinePCRColumnAppearance &&
      this.getVaccinePCRColumnAppearance.scanHost
    ) {
      this.isFlowHasHost = true;
    }

    if (this.getSessionData.data && this.getSessionData.data.data) {
      this.getSessionData.data.data.map((row) => {
        if (row.host && row.host != null) {
          this.isUserHasHost = true;
        }
      });
    }

    if (this.getSessionData.data && this.getSessionData.data.session) {
      if (this.getSessionData.data.session.type) {
        this.fileName = this.getSessionData.data.session.type;
      }
      this.areasMobViewData = this.getSessionData.data.session.areas;
      this.getSessionData.data.session.areas.map((row) => {
        if (
          !this.sitesMobViewData.some(
            (siteSelected) => siteSelected.id == row.site.id
          )
        ) {
          this.sitesMobViewData.push(row.site);
        }
      });
    }

    document.addEventListener("mousedown", (e) => {
      if (e.path) {
        let isColumnChooser = false;
        for (let i = 0; i < e.path.length; i++) {
          if (typeof e.path[i].matches == "function") {
            if (e.path[i].matches(".dx-overlay-content")) {
              isColumnChooser = true;
            }
          }
        }
        if (!isColumnChooser) this.dataGrid.hideColumnChooser();
      }
    });

    // let entityId = this.currentEntity.id;
    console.log("socket connected: ", this.$socket.connected);
    this.$socket.emit(
      "message",
      "Socket Works From EmergencySessionListData!!!"
    );
    this.$socket.emit("join", { session: this.id });
  },
  mounted() {
    window.onresize = () => {
      this.windowWidth = window.innerWidth;
    };
  },
  // sockets: {
  //     connect() {
  //         // Fired when the socket connects.
  //         console.log("server connected");
  //     },
  //     disconnect() {
  //         console.log("server disconnected");
  //     },
  //     update(data) {
  //         console.log("update row", data);

  //         this.fetchSessionData({ id: this.id , params : this.filterBy })
  //         this.dataGrid.refresh().then(function () {
  //             console.log("refreshed");
  //         });

  //     },
  //     welcome(data) {
  //         console.log("welcome", data);
  //     },
  //     pongEndSessionDiffAdmin(data) {
  //         if(data.data.user.email != this.userEmail){
  //             this.sessionEnded = true;
  //             this.fetchSessionData({
  //                 id: this.id,
  //                 params : this.filterBy
  //             }).then(() => {
  //                 this.sessionStatus();
  //             });
  //             this.dataGrid.refresh().then(function () {
  //                 console.log("refreshed");
  //             });
  //             // console.log("pongEndSessionDiffAdmin", data.data);
  //             this.diffAdminEndSession = data.data.user.fullName;
  //             this.$bvModal.show("endSessionWithDiffAdminModal");
  //         }
  //     },
  // },
};
</script>

<style lang="scss" scoped>
.custom-control-input:checked .custom-control-label::before {
  color: #fff;
  width: 20px;
  height: 20px;
  border-color: #c7c7c7 !important;
  background: #c7c7c7 !important;
  opacity: 0.3;
}
.custom-control-input:checked .custom-control-label::before {
  color: #fff;
  width: 20px;
  height: 20px;
  border-color: #c7c7c7 !important;
  background: #c7c7c7 !important;
  opacity: 0.3;
}

button.btn.d-flex.align-items-center.justify-content-center.text-center.session-btn.btn-primary.btn-sm.rounded-pill {
  margin-inline-start: 40px;
  height: 27px;
  width: 127px;

  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  text-align: center;

  color: #ffffff;
}

p {
  font-size: 12px;
  color: #b9b7b7;
}

img {
  height: 27px;
  width: 27px;
}

.emergency-data-list-table {
  tbody {
    color: #495057;
  }
}

.imageMobView {
  margin-bottom: 92px;
}

.headerInMobView {
  margin-bottom: 10px;
}

.endSessionInMobView {
  margin-inline-start: 11px !important;
}

.userImageInMobView {
  display: contents;
}

.userInformationInMobView {
  margin-inline-start: 15px;
}
</style>

<style lang="scss">
.emergency-table {
  .dx-column-indicators {
    float: inline-end !important;
  }
  .breadcrumb-item a {
    color: #4a4a4a;
  }
  .page-top-header {
    width: auto !important;
  }
  ol {
    margin: 0 !important;
    padding-inline-start: 24px !important;
    padding-inline-end: 0 !important;
  }
  .breadcrumb a {
    padding: 0;
  }
}
label {
  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 19px !important;
  color: #4a4a4a;
}
.text-danger label,
.text-danger span {
  color: #f46a6a !important;
}
.text-success label,
.text-success span {
  color: #34c38f !important;
}
.text-blue label,
.text-blue span {
  color: #103f5e !important;
}
.selected label {
  font-weight: 700 !important;
}
#end-session-icon .arrow {
  display: none;
}
.tooltip-inner {
  width: 135px;
  height: 105px;
  margin-top: 5px;
  margin-inline-start: 26px;
}

.no-content {
  background: #ffffff;
  display: flex;
  // align-items: center;
  justify-content: center;
  height: 300px;
  color: #495057;
  p {
    margin-top: 100px;
    color: #495057;

    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 19px;
    text-align: center;
  }
}

div#gridEmergencySessionData {
  label.custom-control-label {
    position: relative;
    padding-inline-start: 9px;
  }
  .custom-radio .custom-control-label::before {
    color: #fff;
    width: 20px;
    height: 20px;
    border-color: #c7c7c7 !important;
    background: #c7c7c7 !important;
    opacity: 0.3;
  }
  .first-radio {
    .custom-control-input:checked ~ .custom-control-label::before {
      color: #fff;
      width: 20px;
      height: 20px;
      border-color: #c7c7c7 !important;
      background: #c7c7c7 !important;
      opacity: 0.3;
    }
    .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
      background-image: none;
      background: #34c38f !important;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      position: absolute;
      top: 5.5px;
      inset-inline-start: -20px;
    }
  }
  .second-radio {
    .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
      background-image: none;
      background: #f46a6a !important;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      position: absolute;
      top: 5.5px;
      inset-inline-start: -20px;
    }
  }
  .third-radio {
    .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
      background-image: none;
      background: #103f5e !important;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      position: absolute;
      top: 5.5px;
      inset-inline-start: -20px;
    }
  }

  label.custom-control-label {
    position: relative;
    padding-inline-start: 9px;
  }
  .custom-radio .custom-control-label::before {
    color: #fff;
    width: 20px;
    height: 20px;
    border-color: #c7c7c7 !important;
    background: #c7c7c7 !important;
    opacity: 0.3;
  }
  .custom-control-input:checked ~ .custom-control-label::before {
    color: #fff;
    width: 20px;
    height: 20px;
    border-color: #c7c7c7 !important;
    background: #c7c7c7 !important;
    opacity: 0.3;
  }
  .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
    background-image: none;
    background: #34c38f;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    position: absolute;
    top: 5.5px;
    inset-inline-start: -20px;
  }
  .dx-widget.dx-dropdownbutton.dx-dropdownbutton-has-arrow {
    margin-inline-end: 20px;
  }
}

div#gridEmergencySessionDataMobView {
  label.custom-control-label {
    position: relative;
    padding-inline-start: 9px;
  }
  .custom-radio .custom-control-label::before {
    color: #fff;
    width: 20px;
    height: 20px;
    border-color: #c7c7c7 !important;
    background: #c7c7c7 !important;
    opacity: 0.3;
  }
  .first-radio {
    .custom-control-input:checked ~ .custom-control-label::before {
      color: #fff;
      width: 20px;
      height: 20px;
      border-color: #c7c7c7 !important;
      background: #c7c7c7 !important;
      opacity: 0.3;
    }
    .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
      background-image: none;
      background: #34c38f !important;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      position: absolute;
      top: 5.5px;
      inset-inline-start: -20px;
    }
  }
  .second-radio {
    .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
      background-image: none;
      background: #f46a6a !important;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      position: absolute;
      top: 5.5px;
      inset-inline-start: -20px;
    }
  }
  .third-radio {
    .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
      background-image: none;
      background: #103f5e !important;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      position: absolute;
      top: 5.5px;
      inset-inline-start: -20px;
    }
  }

  label.custom-control-label {
    position: relative;
    padding-inline-start: 9px;
  }
  .custom-radio .custom-control-label::before {
    color: #fff;
    width: 20px;
    height: 20px;
    border-color: #c7c7c7 !important;
    background: #c7c7c7 !important;
    opacity: 0.3;
  }
  .custom-control-input:checked ~ .custom-control-label::before {
    color: #fff;
    width: 20px;
    height: 20px;
    border-color: #c7c7c7 !important;
    background: #c7c7c7 !important;
    opacity: 0.3;
  }
  .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
    background-image: none;
    background: #34c38f;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    position: absolute;
    top: 5.5px;
    inset-inline-start: -20px;
  }
  // .dx-datagrid {
  //     margin-bottom: 109px!important;
  // }

  .dx-item.dx-toolbar-item.dx-toolbar-button {
    padding-inline-start: 4px !important;
  }

  .dx-widget.dx-button.dx-button-mode-outlined.dx-button-danger.dx-button-has-text.clear-CustomColums.filter-mobile-view {
    margin-inline-start: 0px !important;
  }
  tr.dx-row.dx-data-row.dx-row-lines.dx-column-lines {
    white-space: pre-wrap;
  }
}

div#endSessionModal {
  // background: #F3F3F3;

  div#endSessionModal___BV_modal_content_ {
    width: 500px;
    border-radius: 26px;
    margin-top: 250px;
  }
  .modal-content .modal-body {
    padding: 0px !important;
    border-radius: 26px;
    p.header-title {
      font-weight: 700;
      font-size: 17px;
      line-height: 20px;
      text-align: center;
      margin: 40px auto 40px auto;
      color: #000000;
    }

    .h-line {
      opacity: 1;
      background: #ebebeb;
      width: 420px;
      margin: 0px auto 20px auto;
    }
    .action-btns {
      margin-bottom: 20px;
      .start-btn {
        background: #103f5e;
        border-radius: 16.6275px;
        width: 111px;
        height: 33px;
        color: #ffffff;

        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        line-height: 19px;
        text-align: center;
        margin-inline-end: 31px;
      }
      .cancel-btn {
        border: 1.5px solid #f46a6a;
        border-radius: 16.6275px;
        color: #f46a6a;

        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        line-height: 19px;
        text-align: center;
      }
    }
  }
  .modal-content .modal-body {
    p.header-title {
      font-style: normal;
      font-weight: 700;
      font-size: 17px;
      line-height: 20px;
      margin: 40px auto 40px auto;
      color: #000000;
    }
    .hint-modal {
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      margin: 0px auto 40px auto;
      text-align: center;
      color: #787878;
    }

    .action-btns {
      margin-bottom: 20px;
      .start-btn {
        background: #103f5e;
        border-radius: 16.6275px;
        width: 111px;
        height: 33px;
        color: #ffffff;

        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        line-height: 19px;
        text-align: center;
        margin-inline-end: 31px;
      }
      .cancel-btn {
        border: 1.5px solid #f46a6a;
        border-radius: 16.6275px;
        color: #f46a6a;

        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        line-height: 19px;
        text-align: center;
      }
    }
  }
}

.emergency-session-data-white-bg-sec {
  background-color: white;
  padding-inline-start: 0px;
  // padding-top: 25px;
  border-radius: 8px !important;
}
.closeEndSessionButton {
  cursor: pointer;
}
</style>
